"use client";

import Link from "next/link";
import { Check, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";
import {
  PRICING_CONFIG,
  ANNUAL_PRICING,
  type BillingPeriod,
} from "@/lib/pricing-config";

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: string | ReactNode;
  priceAnnual?: string;
  year1: string;
  year2plus: string;
  popular?: boolean;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
}

interface ComparisonFeature {
  name: string;
  starter: string | boolean;
  pro: string | boolean;
  enterprise: string | boolean;
}

export default function PricingPage() {
  const { t, locale } = useI18n();
  const [billing, setBilling] = useState<BillingPeriod>("annual");

  function rubPrice(tier: "starter" | "pro"): string {
    const rub = billing === "annual"
      ? ANNUAL_PRICING[tier].rubPerMonth
      : PRICING_CONFIG[tier].rub;
    return `₽${rub.toLocaleString("ru-RU")}`;
  }

  function usdPrice(tier: "starter" | "pro"): string {
    const usd = billing === "annual"
      ? ANNUAL_PRICING[tier].usdPerMonth
      : PRICING_CONFIG[tier].usd;
    return `$${usd}`;
  }

  function priceForTier(tier: "starter" | "pro"): string {
    return locale === "en" ? usdPrice(tier) : rubPrice(tier);
  }

  const isAnnual = billing === "annual";

  const starterYear1 = isAnnual
    ? `₽${ANNUAL_PRICING.starter.rubPerYear.toLocaleString("ru-RU")}`
    : `₽${(PRICING_CONFIG.starter.rub * 12).toLocaleString("ru-RU")}`;
  const proYear1 = isAnnual
    ? `₽${ANNUAL_PRICING.pro.rubPerYear.toLocaleString("ru-RU")}`
    : `₽${(PRICING_CONFIG.pro.rub * 12).toLocaleString("ru-RU")}`;

  const tiers: PricingTier[] = [
    {
      id: "starter",
      name: t("landing.pricing.starter.name") || "Starter",
      description: t("landing.pricing.starter.desc") || "1 объект · до 100 номеров · до 6 revenue centers",
      price: priceForTier("starter"),
      year1: starterYear1,
      year2plus: starterYear1,
      popular: false,
      features: [
        t("landing.pricing.starter.f1") || "USALI базовый (S1–S6)",
        t("landing.pricing.starter.f2") || "My-tasks и Approvals (1 уровень)",
        t("landing.pricing.starter.f3") || "KPI dashboard",
        t("landing.pricing.starter.f4") || "До 5 пользователей",
        t("landing.pricing.starter.f5") || "Email-поддержка",
      ],
      ctaLabel: t("landing.pricing.demoCta") || "Запросить демо",
      ctaHref: "/contact",
    },
    {
      id: "pro",
      name: t("landing.pricing.pro.name") || "Pro",
      description: t("landing.pricing.pro.desc") || "До 3 объектов · до 300 номеров · до 15 revenue centers",
      price: priceForTier("pro"),
      year1: isAnnual
        ? `₽${ANNUAL_PRICING.pro.rubPerYear.toLocaleString("ru-RU")} + внедрение в подарок`
        : `${proYear1} + ₽150 000 внедрение`,
      year2plus: proYear1,
      popular: true,
      features: [
        t("landing.pricing.pro.f1") || "Всё из Starter +",
        t("landing.pricing.pro.f2") || "USALI полный (S1–S13)",
        t("landing.pricing.pro.f3") || "Multi-step approval chain (3 уровня)",
        t("landing.pricing.pro.f4") || "Procurement с PO lifecycle",
        t("landing.pricing.pro.f5") || "Multi-property consolidated reporting",
        t("landing.pricing.pro.f6") || "Audit trail (156-ФЗ)",
        t("landing.pricing.pro.f7") || "До 25 пользователей",
        t("landing.pricing.pro.f8") || "Приоритетная поддержка + Slack",
      ],
      ctaLabel: t("landing.pricing.demoCta") || "Запросить демо",
      ctaHref: "/contact",
    },
    {
      id: "enterprise",
      name: t("landing.pricing.enterprise.name") || "Enterprise",
      description: t("landing.pricing.enterprise.desc") || "300+ номеров · 15+ revenue centers · сеть 3+ объектов",
      price: t("landing.pricing.enterprise.price") || "По запросу",
      year1: "индивидуально",
      year2plus: "индивидуально",
      popular: false,
      features: [
        t("landing.pricing.enterprise.f1") || "Всё из Pro +",
        t("landing.pricing.enterprise.f2") || "1С интеграция (roadmap Q3)",
        t("landing.pricing.enterprise.f3") || "SSO (SAML / OIDC)",
        t("landing.pricing.enterprise.f4") || "SLA 99.95%",
        t("landing.pricing.enterprise.f5") || "Dedicated CSM",
        t("landing.pricing.enterprise.f6") || "Кастомные отчёты и интеграции",
        t("landing.pricing.enterprise.f7") || "On-premise опция",
      ],
      ctaLabel: t("landing.pricing.salesCta") || "Связаться с продажами",
      ctaHref: "/contact",
    },
  ];

  const comparisonFeatures: ComparisonFeature[] = [
    { name: "USALI Schedule S1–S6",             starter: true,         pro: true,            enterprise: true },
    { name: "USALI Schedule S7–S13",             starter: false,        pro: true,            enterprise: true },
    { name: "Approvals (уровни)",                starter: "1",          pro: "3",             enterprise: "Неограниченно" },
    { name: "Multi-property reporting",          starter: false,        pro: true,            enterprise: true },
    { name: "Audit trail (156-ФЗ)",              starter: false,        pro: true,            enterprise: true },
    { name: "Procurement + PO lifecycle",        starter: false,        pro: true,            enterprise: true },
    { name: "1С интеграция",                     starter: false,        pro: "Базовая",       enterprise: "Полная" },
    { name: "SSO (SAML / OIDC)",                 starter: false,        pro: false,           enterprise: true },
    { name: "SLA",                               starter: "Best effort",pro: "Best effort",  enterprise: "99.95%" },
    { name: "Dedicated Customer Success Manager",starter: false,        pro: false,           enterprise: true },
    { name: "Пользователи",                      starter: "до 5",       pro: "до 25",         enterprise: "Неограниченно" },
  ];

  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("landing.pricing.headline") || "Цены Keiront"}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t("landing.pricing.subheading") || "Прозрачно. Per-property. Без скрытых платежей."}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billing === "monthly"
                  ? "bg-white shadow text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t("landing.pricing.monthly") || "Месяц"}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billing === "annual"
                  ? "bg-white shadow text-slate-900"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t("landing.pricing.annual") || "Год"}
              <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                −25%
              </span>
            </button>
          </div>
        </div>

        {/* Pilot Banner */}
        <div className="mb-12 max-w-2xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <p className="text-amber-900 font-semibold">
              {t("landing.pricing.pilotBanner") || "🎁 Для первых pilot-клиентов — специальные условия."}
            </p>
            <Button asChild variant="outline" className="text-amber-700 hover:text-amber-900 border-amber-300 mt-2">
              <Link href="/contact">
                {t("landing.pricing.demoCta") || "Запросить демо"}
              </Link>
            </Button>
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`rounded-xl border transition-all duration-300 flex flex-col ${
                tier.popular
                  ? "border-2 border-keiro-green bg-white shadow-lg md:relative md:-top-4"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-keiro-green text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {t("landing.pricing.popular") || "ПОПУЛЯРНЫЙ"}
                  </span>
                </div>
              )}

              <div className="p-8 border-b border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-1">{tier.name}</h3>
                <p className="text-xs text-slate-500 mb-6">{tier.description}</p>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                    {tier.id !== "enterprise" && (
                      <span className="text-slate-500 text-sm">/мес</span>
                    )}
                  </div>
                  {tier.id !== "enterprise" && isAnnual && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                      −25% при оплате за год
                    </div>
                  )}
                  {tier.id === "enterprise" && (
                    <p className="text-sm text-slate-500 mt-1">Обсудим условия индивидуально</p>
                  )}
                </div>

                {/* Year 1 / Year 2+ */}
                {tier.id !== "enterprise" && (
                  <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Год 1:</span>
                      <span className="font-semibold text-slate-800">{tier.year1}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Год 2+:</span>
                      <span className="font-semibold text-slate-800">{tier.year2plus}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 p-8">
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check size={16} className="text-keiro-green flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 border-t border-slate-200">
                <Button asChild variant={tier.popular ? "default" : "outline"} className="w-full h-11">
                  <a href={tier.ctaHref}>{tier.ctaLabel}</a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Implementation Table */}
        <div className="mb-20 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
            {t("landing.pricing.implTitle") || "Внедрение и запуск"}
          </h2>
          <p className="text-slate-500 text-sm text-center mb-6">
            {t("landing.pricing.implSubtitle") || "Разовый платёж за настройку, обучение и запуск системы"}
          </p>
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">Тариф</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">Формат</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">Стоимость</th>
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">Срок</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="px-6 py-4 font-medium text-slate-900">Starter</td>
                  <td className="px-6 py-4 text-slate-600">Self-service + видеокурс</td>
                  <td className="px-6 py-4 font-semibold text-emerald-700">Бесплатно</td>
                  <td className="px-6 py-4 text-slate-500">~2 недели</td>
                </tr>
                <tr className="border-b border-slate-100 bg-emerald-50/30">
                  <td className="px-6 py-4 font-medium text-slate-900">Pro</td>
                  <td className="px-6 py-4 text-slate-600">Guided onboarding с CSM</td>
                  <td className="px-6 py-4">
                    {isAnnual ? (
                      <span className="font-semibold text-emerald-700">
                        Включено при годовой оплате
                        <span className="block text-xs text-slate-400 font-normal">обычно ₽150 000</span>
                      </span>
                    ) : (
                      <span className="font-semibold text-slate-800">₽150 000</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-500">~4 недели</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-slate-900">Enterprise</td>
                  <td className="px-6 py-4 text-slate-600">Полный проект, выделенная команда</td>
                  <td className="px-6 py-4 font-semibold text-slate-800">от ₽500 000</td>
                  <td className="px-6 py-4 text-slate-500">8–12 недель</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            {t("landing.pricing.comparisonTitle") || "Полное сравнение тарифов"}
          </h2>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-6 py-4 font-semibold text-slate-900">
                    {t("landing.pricing.feature") || "Функция"}
                  </th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-900">Starter</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-900">Pro</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-900">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, idx) => (
                  <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-6 py-4 text-slate-900 font-medium">{feature.name}</td>
                    {(["starter", "pro", "enterprise"] as const).map((col) => (
                      <td key={col} className="text-center px-6 py-4">
                        {typeof feature[col] === "boolean" ? (
                          feature[col] ? (
                            <Check size={20} className="inline text-keiro-green" />
                          ) : (
                            <X size={16} className="inline text-slate-300" />
                          )
                        ) : (
                          <span className="text-slate-700 text-sm">{feature[col]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            {t("landing.pricing.faqTitle") || "Часто задаваемые вопросы"}
          </h2>
          <div className="space-y-6">
            {[
              {
                q: t("landing.pricing.faq1.q") || "Какую модель биллинга вы используете?",
                a: t("landing.pricing.faq1.a") || "Per-property: вы платите за каждый объект отдельно. Тариф включает всё — нет скрытых платежей за интеграции или экспорты.",
              },
              {
                q: t("landing.pricing.faq2.q") || "Можно ли менять тариф в течение года?",
                a: t("landing.pricing.faq2.a") || "Да. Вы можете масштабировать тариф вверх или вниз в любой момент. Переплата пересчитывается пропорционально дням.",
              },
              {
                q: t("landing.pricing.faq3.q") || "Какая скидка на годовую оплату?",
                a: t("landing.pricing.faq3.a") || "25% при оплате за год. Для Pro это ещё выгоднее: при годовом контракте внедрение (обычно ₽150 000) включено в стоимость.",
              },
              {
                q: t("landing.pricing.faq4.q") || "Что входит в onboarding?",
                a: t("landing.pricing.faq4.a") || "Starter — самостоятельный запуск по видеокурсу (2 недели). Pro — guided onboarding с выделенным CSM: настройка счётного плана, USALI-маппинг, обучение команды (4 недели). Enterprise — полный проект 8–12 недель.",
              },
              {
                q: t("landing.pricing.faq5.q") || "Как работает pilot-программа?",
                a: t("landing.pricing.faq5.a") || "Первые клиенты получают специальные условия: приоритетный onboarding и прямой feedback-loop с продуктом. Свяжитесь с отделом продаж.",
              },
            ].map((item, idx) => (
              <details
                key={idx}
                className="group border border-slate-200 rounded-lg p-6 cursor-pointer hover:border-slate-300"
              >
                <summary className="flex items-center justify-between font-semibold text-slate-900 outline-none">
                  {item.q}
                  <span className="ml-4 text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-slate-50 rounded-xl p-12 text-center border border-slate-200">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("landing.pricing.finalCtaTitle") || "Не уверены, какой тариф подходит?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("landing.pricing.finalCtaDesc") || "Запросите демо — наша команда поможет выбрать оптимальное решение."}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("landing.pricing.demoCta") || "Запросить демо"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
